import { Table, Tag } from 'antd';
import { SafetyCertificateOutlined } from '@ant-design/icons';
import { FormSectionTitle } from '@leedpulse-monorepo/components';
import { IAppLeedflow } from '@leedpulse-monorepo/interfaces';
import moment from 'moment';

/**
 * Autorisations relevées par l'app mobile au dernier heartbeat (LEED-2109).
 * Les clés sont les `PermissionKind` de
 * `mobile/src/domain/permissions/types.ts` : la liste ci-dessous les ordonne,
 * les traduit et dit sur quelle plateforme chacune existe. Une clé inconnue
 * (app plus récente que le back-office) est ignorée.
 *
 * On filtre sur la plateforme de l'appareil plutôt que sur le statut
 * `not_applicable` : une ligne absente ne dit pas si l'autorisation n'existe
 * pas sur cet appareil ou si l'app est trop vieille pour la relever. Le
 * support voit donc toujours la même liste pour une plateforme donnée, avec
 * « Non applicable » quand il n'y a rien à accorder (ex. Photos sur Android
 * 13+, qui passe par le Photo Picker système).
 */
type PermissionLabel = {
  key: string;
  label: string;
  platforms: ('ios' | 'android')[];
};

const PERMISSION_LABELS: PermissionLabel[] = [
  { key: 'microphone', label: 'Microphone', platforms: ['ios', 'android'] },
  { key: 'notifications', label: 'Notifications', platforms: ['ios', 'android'] },
  { key: 'contacts', label: 'Contacts', platforms: ['ios', 'android'] },
  { key: 'photos', label: 'Photos', platforms: ['ios', 'android'] },
  { key: 'callkit', label: 'CallKit', platforms: ['ios'] },
  { key: 'callDirectory', label: 'Identification des appels', platforms: ['ios'] },
  { key: 'phoneState', label: 'Téléphone (état / numéro)', platforms: ['android'] },
  { key: 'callAccount', label: "Compte d'appels", platforms: ['android'] },
  { key: 'batteryOpt', label: 'Batterie sans restriction', platforms: ['android'] },
  { key: 'fullScreenIntent', label: 'Appels plein écran', platforms: ['android'] },
  {
    key: 'miuiLockScreen',
    label: 'Xiaomi : fenêtres en arrière-plan + écran verrouillé',
    platforms: ['android'],
  },
];

/**
 * Jamais de vert sur autre chose que `granted` : `unknown` veut dire qu'on n'a
 * pas pu lire l'autorisation (pas de bridge natif), pas qu'elle est accordée.
 */
const STATUS_TAGS: Record<string, { color: string; label: string }> = {
  granted: { color: 'green', label: 'Accordée' },
  denied: { color: 'red', label: 'Refusée' },
  permanently_denied: { color: 'red', label: 'Refusée définitivement' },
  undetermined: { color: 'default', label: 'Jamais demandée' },
  unknown: { color: 'default', label: 'Inconnue' },
  not_applicable: { color: 'default', label: 'Non applicable' },
};

interface UserAppPermissionsProps {
  appLeedflow: IAppLeedflow | null;
}

const UserAppPermissions: React.FC<UserAppPermissionsProps> = ({ appLeedflow }) => {
  const permissions = appLeedflow?.permissions;
  const platform = appLeedflow?.platform === 'ios' || appLeedflow?.platform === 'android'
    ? appLeedflow.platform
    : null;
  const rows = PERMISSION_LABELS
    .filter(({ key, platforms }) => {
      if (!permissions?.[key]) return false;
      // Plateforme inconnue (vieux relevé) : on retombe sur le statut, seul
      // signal disponible pour ne pas afficher les lignes de l'autre OS.
      if (!platform) return permissions[key] !== 'not_applicable';
      return platforms.includes(platform);
    })
    .map(({ key, label }) => ({ key, label, status: permissions?.[key] as string }));

  const columns = [
    {
      title: 'Autorisation',
      key: 'label',
      render: (record: { label: string }) => record.label,
    },
    {
      title: 'Statut',
      key: 'status',
      render: (record: { status: string }) => {
        const tag = STATUS_TAGS[record.status];
        return tag ? (
          <Tag color={tag.color}>{tag.label}</Tag>
        ) : (
          <Tag>{record.status}</Tag>
        );
      },
    },
  ];

  return (
    <>
      <FormSectionTitle
        title={
          <span>
            <SafetyCertificateOutlined style={{ marginRight: 6 }} />
            Autorisations
          </span>
        }
      />
      <div className="users-card-companies-container">
        {rows.length > 0 ? (
          <>
            <Table
              pagination={false}
              columns={columns}
              rowKey={(record) => record.key}
              dataSource={rows}
              size="small"
            />
            <p style={{ marginTop: 8, fontSize: '14px', color: 'gray' }}>
              {appLeedflow?.permissions_checked_at
                ? `Relevé ${moment(appLeedflow.permissions_checked_at).fromNow()}`
                : 'Date du relevé inconnue'}
            </p>
          </>
        ) : (
          <p style={{ textAlign: 'center', fontSize: '14px', color: '#888' }}>
            Aucun relevé : l'app mobile n'a pas encore remonté ses autorisations.
          </p>
        )}
      </div>
    </>
  );
};

export default UserAppPermissions;
